import React, { useState } from "react";
import moment from "moment";
import {
  LuBuilding2,
  LuCalendar,
  LuDatabase,
  LuPencil,
  LuTags,
  LuTrash2,
  LuWallet,
} from "react-icons/lu";

import Badge from "../ui/Badge";
import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import {
  PartiesProps,
  PartySortBy,
  PartySortOrder,
} from "../../types/parties";
import SortableTableHead from "../ui/SortableTableHead";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditParty,
  togglePartyModal,
} from "../../features/parties/partiesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { Link, useNavigate } from "react-router-dom";
import { partyTypeBadgeClass, partyTypeLabel } from "../../static/partyType";
import { partyProcessTypes } from "../../static/processRate";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isOverCreditLimit = (party: PartiesProps) =>
  party.credit_limit > 0 &&
  Math.round((party.total_amount || 0) * 100) >
    Math.round(party.credit_limit * 100);

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const typeBadges = (types: string[]) =>
  types.map((type) => (
    <Badge key={type} variant="none" className={partyTypeBadgeClass(type)}>
      {partyTypeLabel(type)}
    </Badge>
  ));

const inhouseBadge = (isInhouse: number) =>
  isInhouse === 1 ? (
    <Badge
      variant="none"
      className="bg-[#113F67]/10 text-[#113F67] hover:bg-[#113F67]/10"
    >
      ઇનહાઉસ
    </Badge>
  ) : null;

const statusBadge = (status: number) =>
  status === 1 ? (
    <Badge
      variant="none"
      className="bg-green-100 text-green-800 hover:bg-green-100"
    >
      સક્રિય
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      નિષ્ક્રિય
    </Badge>
  );

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

// Rates are kept per process type, so a party with none of them has nothing to set.
const hasRateTypes = (party: PartiesProps) =>
  partyProcessTypes(party.type).length > 0;

const noRateTypesTitle = "ભાવ માટે પાર્ટીનો પ્રકાર કટીંગ, ટર્નિંગ કે VMC હોવો જોઈએ";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

type Props = {
  parties: PartiesProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  // The order the parties were loaded in.
  sortBy: PartySortBy;
  sortOrder: PartySortOrder;
  onSort: (sortBy: PartySortBy, sortOrder: PartySortOrder) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<PartySortBy, string>;
  handleDeleteParty: (id: number) => void;
};

const PartyList = ({
  parties,
  pageNumber,
  pageSize,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  handleDeleteParty,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.partiesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.partiesDelete
  );
  const canItems = hasPermission(
    loggingUser?.permissions,
    permission.partiesItems
  );
  const canPayments = hasPermission(
    loggingUser?.permissions,
    permission.paymentsRead
  );

  const actionCount =
    (canPayments ? 1 : 0) +
    (canItems ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);
  const hasActions = actionCount > 0;
  const tableColSpan = 8 + (hasActions ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  // The date header shows both dates, so it also stands for the last-changed date the filter can pick.
  const sortHead = (
    column: PartySortBy,
    label: string,
    className?: string
  ) => (
    <SortableTableHead
      column={column}
      alsoFor={column === "created_at" ? ["updated_at"] : undefined}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortLabels={sortLabels}
      disabled={isLoading}
      className={className}
    >
      {label}
    </SortableTableHead>
  );

  const handleEditParty = async (party: PartiesProps) => {
    await dispatch(setEditParty(party));
    dispatch(togglePartyModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteParty(id);
      setIsActionLoading(0);
    }
  };

  const handleOpenRates = (id: number) => {
    navigate(route.partyRates.replace(":id", String(id)));
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {sortHead("id", "ક્રમ")}
                  <TableHead className={tableHeadCellClass}>પ્રકાર</TableHead>
                  {sortHead("firm_name", "પેઢી નું નામ")}
                  {sortHead("name", "પાર્ટી નું નામ")}
                  <TableHead className={tableHeadCellClass}>
                    ફોન નંબર
                  </TableHead>
                  {sortHead("total_amount", "પેમેન્ટ", "text-right")}
                  {sortHead("status", "સ્ટેટ્સ")}
                  {sortHead("created_at", "તારીખ")}
                  {hasActions && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : parties.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="પાર્ટી ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {parties.map((party, index) => (
                    <TableRow key={party.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        {party.type?.length ? (
                          <div className="flex gap-1">
                            {typeBadges(party.type)}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <LuBuilding2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="flex flex-wrap items-center gap-2 font-medium">
                              {party.firm_name || "-"}
                              {inhouseBadge(party.is_inhouse)}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {party.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>{party.name || "-"}</TableCell>

                      <TableCell>
                        <div className="font-medium">{party.phone || "-"}</div>
                        {party.wp_phone && (
                          <div className="text-sm text-gray-500">
                            WP: {party.wp_phone}
                          </div>
                        )}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(party.total_amount)}
                      </TableCell>

                      <TableCell>{statusBadge(party.status)}</TableCell>

                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(party.created_at)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDate(party.updated_at)}
                          </span>
                        </div>
                      </TableCell>

                      {hasActions && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === party.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {canPayments && (
                                  <Link
                                    to={route.payments.replace(
                                      ":id",
                                      party.id as unknown as string
                                    )}
                                  >
                                    <Button
                                      variant="ghost"
                                      size="iconSm"
                                      title="પેમેન્ટ"
                                      className={cx(
                                        isOverCreditLimit(party) &&
                                          "text-red-600 hover:text-red-700"
                                      )}
                                    >
                                      <LuWallet className="h-4 w-4" />
                                    </Button>
                                  </Link>
                                )}

                                {canItems && (
                                  <span
                                    className="inline-flex"
                                    title={
                                      hasRateTypes(party)
                                        ? undefined
                                        : noRateTypesTitle
                                    }
                                  >
                                    <Button
                                      variant="ghost"
                                      size="iconSm"
                                      title="ભાવ"
                                      disabled={!hasRateTypes(party)}
                                      onClick={() => handleOpenRates(party.id)}
                                    >
                                      <LuTags className="h-4 w-4" />
                                    </Button>
                                  </span>
                                )}

                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() => handleEditParty(party)}
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteRow(party.id)}
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : parties.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="પાર્ટી ની વિગતો મળી નથી" />
          </div>
        ) : (
          parties.map((party, index) => (
            <div key={party.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                <div className="flex min-w-0 items-center gap-2">
                  <LuBuilding2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 font-medium break-words">
                      {party.firm_name || "-"}
                      {inhouseBadge(party.is_inhouse)}
                    </div>
                    <div className="text-sm text-gray-500">ID: {party.id}</div>
                  </div>
                </div>

                {statusBadge(party.status)}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પ્રકાર</span>
                  {party.type?.length ? (
                    <div className="flex min-w-0 flex-wrap justify-end gap-1">
                      {typeBadges(party.type)}
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પાર્ટી નું નામ</span>
                  <span className="min-w-0 text-right break-words">
                    {party.name || "-"}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફોન નંબર</span>
                  <div className="min-w-0 text-right break-words">
                    <div className="font-medium">{party.phone || "-"}</div>
                    {party.wp_phone && (
                      <div className="text-gray-500">WP: {party.wp_phone}</div>
                    )}
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પેમેન્ટ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(party.total_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(party.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDate(party.updated_at)}
                    </span>
                  </span>
                </div>
              </div>

              {hasActions && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionGridClass[actionCount]
                  )}
                >
                  {isActionLoading === party.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {canPayments && (
                        <Link
                          to={route.payments.replace(
                            ":id",
                            party.id as unknown as string
                          )}
                        >
                          <Button
                            variant="outline"
                            className={cx(
                              "w-full",
                              isOverCreditLimit(party) &&
                                "text-red-600 hover:text-red-700"
                            )}
                            title="પેમેન્ટ"
                          >
                            <LuWallet className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}

                      {canItems && (
                        <span
                          className="block"
                          title={
                            hasRateTypes(party) ? undefined : noRateTypesTitle
                          }
                        >
                          <Button
                            variant="outline"
                            className="w-full"
                            title="ભાવ"
                            disabled={!hasRateTypes(party)}
                            onClick={() => handleOpenRates(party.id)}
                          >
                            <LuTags className="h-4 w-4" />
                          </Button>
                        </span>
                      )}

                      {canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditParty(party)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          title="કાઢી નાખો"
                          onClick={() => handleDeleteRow(party.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default PartyList;
